/**
 * Search.tsx — Busca textual cross-vault (/search?q=...)
 *
 * Busca por título e conteúdo nas vault_notes do Firestore.
 * TODO V2: busca semântica via embeddings.
 */

import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { SearchBar } from '../components/SearchBar'

interface SearchResult {
  id: string
  title: string
  category: string
  path: string
  snippet: string
}

export function Search() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const q = searchParams.get('q') ?? ''
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)

  useEffect(() => {
    if (!q.trim()) return
    runSearch(q)
  }, [q])

  async function runSearch(queryStr: string) {
    setLoading(true)
    setSearched(true)

    try {
      // Busca client-side simples (carrega todas as notas e filtra)
      // TODO V2: Firestore full-text search via Algolia/Typesense ou busca semântica
      const snap = await getDocs(
        query(collection(db, 'vault_notes'), orderBy('title'), limit(1000))
      )

      const lower = queryStr.toLowerCase()
      const found: SearchResult[] = []

      for (const d of snap.docs) {
        const data = d.data()
        const title = (data.title as string) ?? ''
        const content = (data.content_md as string) ?? ''

        if (
          title.toLowerCase().includes(lower) ||
          content.toLowerCase().includes(lower)
        ) {
          // Gera snippet ao redor da primeira ocorrência no conteúdo
          const idx = content.toLowerCase().indexOf(lower)
          let snippet = ''
          if (idx !== -1) {
            const start = Math.max(0, idx - 80)
            const end = Math.min(content.length, idx + lower.length + 120)
            snippet = (start > 0 ? '...' : '') + content.slice(start, end) + (end < content.length ? '...' : '')
          }

          found.push({
            id: d.id,
            title,
            category: (data.category as string) ?? '',
            path: (data.path as string) ?? '',
            snippet,
          })
        }
      }

      setResults(found.slice(0, 50)) // limite de exibição
    } catch (err) {
      console.error('[Search] Erro:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '32px 24px',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <h1 style={{ fontSize: '20px', color: '#e8e8f0', marginBottom: '24px' }}>
        Busca no Vault
      </h1>

      <SearchBar initialQuery={q} placeholder="Buscar notas, conceitos, clientes..." />

      {/* Resultados */}
      <div style={{ marginTop: '32px' }}>
        {loading && (
          <div style={{ color: '#5a5a80', fontSize: '13px' }}>Buscando...</div>
        )}

        {searched && !loading && results.length === 0 && (
          <div style={{ color: '#5a5a80', fontSize: '13px' }}>
            Nenhuma nota encontrada para "{q}".
          </div>
        )}

        {results.map((r) => (
          <div
            key={r.id}
            onClick={() => navigate(`/node/${r.id}`)}
            style={{
              padding: '16px',
              marginBottom: '12px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 150ms ease',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget
              el.style.background = 'rgba(87, 245, 195, 0.06)'
              el.style.borderColor = 'rgba(87, 245, 195, 0.2)'
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget
              el.style.background = 'rgba(255,255,255,0.03)'
              el.style.borderColor = 'rgba(255,255,255,0.06)'
            }}
          >
            <div
              style={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#e8e8f0',
                marginBottom: '4px',
              }}
            >
              {r.title}
            </div>
            <div style={{ fontSize: '11px', color: '#5a5a80', marginBottom: '8px' }}>
              {r.path}
            </div>
            {r.snippet && (
              <div
                style={{
                  fontSize: '12px',
                  color: '#9090b0',
                  lineHeight: 1.5,
                }}
              >
                {r.snippet}
              </div>
            )}
          </div>
        ))}

        {results.length > 0 && (
          <div style={{ fontSize: '11px', color: '#5a5a80', marginTop: '8px' }}>
            {results.length} resultado(s)
            {results.length === 50 ? ' (mostrando top 50)' : ''}
          </div>
        )}
      </div>
    </div>
  )
}
